import {
  browserLocalPersistence,
  browserSessionPersistence,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  User,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { create } from 'zustand';

import { auth, db } from '@/config';
import { detectBrowserLanguage } from '@/utils';
import { unregisterWebPushToken } from '@/utils/webPush';
import { localePath } from '@/utils/locale';

import { useLanguageState } from './languageState';
import { useOrganizationState } from './organizationState';

export type UserRole =
  | 'room_service'
  | 'technical_service'
  | 'house_keeping'
  | 'reception'
  | 'manager'
  | 'spa'
  | null;

export interface UserState {
  // Firebase auth user
  currentUser: User | null;
  userEmail: string | null;

  // User profile data
  userName: string | null;
  avatar: string | null;
  role: UserRole;
  userLanguage: string;
  tutorialCompleted: boolean;

  // Current organization (associated_organization)
  organizationId: string | null;

  // All organizations user belongs to
  organizations: string[] | null;

  // Organization check state
  hasOrganization: boolean | null; // null = not checked yet, true = has org, false = no org

  // Loading states
  isLoading: boolean;
  isResolved: boolean;

  // Actions
  signInWithEmail: (email: string, password: string, rememberMe?: boolean) => Promise<User>;
  signInWithGoogle: (rememberMe?: boolean) => Promise<User>;
  handleRedirectResult: () => Promise<User | undefined>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (code: string, newPassword: string) => Promise<void>;
  setAvatar: (avatar: string | null) => void;
  setUserLanguage: (language: string) => void;
  setUser: (userData: Partial<UserState>) => void;
}

const LS_KEYS = {
  USER_META: 'auth.user.meta',
};

const readLS = <T>(k: string): T | null => {
  try {
    const v = localStorage.getItem(k);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
};

const writeLS = (k: string, v: unknown) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* ignore quota */
  }
};

const initialState = {
  currentUser: null,
  userEmail: null,
  userName: null,
  avatar: null,
  role: null as UserRole,
  userLanguage: detectBrowserLanguage() || 'pl',
  tutorialCompleted: false,
  organizationId: null,
  organizations: null,
  hasOrganization: null,
  isLoading: true,
  isResolved: false,
};

// "Zapamiętaj mnie" steruje trwałością sesji Firebase:
// - zaznaczone  -> browserLocalPersistence: sesja przeżywa zamknięcie przeglądarki,
// - odznaczone  -> browserSessionPersistence: sesja ginie wraz z zamknięciem karty.
// Musi być ustawione PRZED wywołaniem signIn, inaczej nie zadziała dla tej sesji.
const applyPersistence = async (rememberMe: boolean): Promise<void> => {
  try {
    await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
  } catch {
    /* w razie problemu zostaje domyślna trwałość */
  }
};

// Globalny listener dla user
let userUnsub: Unsubscribe | null = null;

// Odroczony reset stanu przy braku usera (patrz onAuthStateChanged).
// Trzymamy uchwyt, żeby móc go anulować gdy przyjdzie kolejne zdarzenie auth —
// Firebase potrafi wyemitować przejściowy `null` tuż po logowaniu (przełączenie
// persystencji / force-refresh tokena), a bez anulowania ten timer zerował
// świeżo zalogowanego usera i wyrzucał go z powrotem na /login.
let noUserResetTimer: ReturnType<typeof setTimeout> | null = null;

const clearUserListener = () => {
  if (userUnsub) {
    userUnsub();
    userUnsub = null;
  }
};

/**
 * Konto personelu panelu ma dokument `users/{uid}`. Goście (aplikacja gościa)
 * korzystają z TEJ SAMEJ puli Firebase Auth, ale są w kolekcji `guests` — nie
 * mają dokumentu `users`. Firebase Auth zaloguje je poprawnie (nie da się tego
 * zablokować na etapie logowania), więc dostęp do panelu odcinamy TUTAJ:
 * po uwierzytelnieniu sprawdzamy istnienie dokumentu personelu. Brak → to nie
 * jest konto panelu: wylogowujemy i zgłaszamy `PanelAccessError`.
 *
 * Reguły Firestore pozwalają czytać WŁASNY `users/{uid}` (isOwner), więc odczyt
 * nie wymaga bycia personelem — sprawdzamy samo istnienie dokumentu.
 */
// Klucz przekazujący monit „brak dostępu" przez wylogowanie + redirect na
// stronę logowania (przeżywa reset stanu). Czyta go i kasuje SignIn.
export const PANEL_ACCESS_DENIED_KEY = 'panel.accessDenied';

export class PanelAccessError extends Error {
  code = 'panel/no-access';

  constructor() {
    super('This account has no panel access');
    this.name = 'PanelAccessError';
  }
}

const assertPanelAccess = async (user: User): Promise<void> => {
  // 1) Custom claim `isStaff` — nadawany WYŁĄCZNIE personelowi (setStaff /
  //    backend). Gość go nie ma. To główny i najtańszy sygnał (bez odczytu bazy).
  const token = await user.getIdTokenResult();
  if (token.claims.isStaff === true) return;

  // 2) Zapas na czas migracji: personel, którego token nie odświeżył jeszcze
  //    claimu, wciąż ma dokument `users/{uid}`. Gość nie ma ani claimu, ani
  //    dokumentu — dopiero brak OBU oznacza „to nie konto panelu".
  const snap = await getDoc(doc(db, 'users', user.uid));
  if (snap.exists()) return;

  await signOut(auth);
  throw new PanelAccessError();
};

export const useUserState = create<UserState>((set, get) => ({
  ...initialState,

  signInWithEmail: async (email, password, rememberMe = false) => {
    set({ isLoading: true });
    try {
      await applyPersistence(rememberMe);
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      // Gość (brak dokumentu users/{uid}) → wyloguj i zgłoś brak uprawnień,
      // ZANIM wpuścimy do panelu. Rzuca PanelAccessError (łapie SignIn).
      await assertPanelAccess(user);

      // Immediately set user state to indicate successful login
      set({
        currentUser: user,
        userEmail: user.email ?? null,
        isLoading: false,
        isResolved: true,
      });

      // Force refresh the ID token to ensure claims are updated
      await user.getIdToken(true);

      return user;
    } catch (error) {
      set({ isLoading: false, isResolved: true });
      throw error;
    }
  },

  signInWithGoogle: async (rememberMe = false) => {
    set({ isLoading: true });
    try {
      await applyPersistence(rememberMe);
      const provider = new GoogleAuthProvider();
      // Tymczasowo używamy signInWithPopup zamiast signInWithRedirect
      // dopóki domena dev.panel.hotelspot.pl nie zostanie dodana do Firebase
      const result = await signInWithPopup(auth, provider);
      const { user } = result;

      // Wykryj język przeglądarki
      const browserLanguage = detectBrowserLanguage();

      // Rola (owner) wynika z ENDPOINTU — backend bierze uid z tokena, nie z body.
      const baseURL = import.meta.env.VITE_URL || 'https://europe-west1-hotelspot-dev.cloudfunctions.net';
      const idToken = await user.getIdToken();
      await fetch(`${baseURL}/v3-processGoogleSignInPanel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ language: browserLanguage }),
      });

      // Force refresh the ID token to ensure claims are updated
      await user.getIdToken(true);

      // Gość bez dostępu do panelu → wyloguj i zgłoś brak uprawnień.
      await assertPanelAccess(user);

      set({
        currentUser: user,
        userEmail: user.email ?? null,
        isLoading: false,
        isResolved: true,
      });

      return user;
    } catch (error) {
      set({ isLoading: false, isResolved: true });
      throw error;
    }
  },

  handleRedirectResult: async () => {
    try {
      const result = await getRedirectResult(auth);
      if (result) {
        const { user } = result;

        // Wykryj język przeglądarki
        const browserLanguage = detectBrowserLanguage();

        // Rola (owner) wynika z ENDPOINTU — backend bierze uid z tokena, nie z body.
        const baseURL = import.meta.env.VITE_URL || 'https://europe-west1-hotelspot-dev.cloudfunctions.net';
        const idToken = await user.getIdToken();
        await fetch(`${baseURL}/v3-processGoogleSignInPanel`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ language: browserLanguage }),
        });

        await user.getIdToken(true);

        // Gość bez dostępu do panelu → wyloguj i zgłoś brak uprawnień.
        await assertPanelAccess(user);

        set({
          currentUser: user,
          userEmail: user.email ?? null,
          isLoading: false,
          isResolved: true,
        });

        // After successful Google Sign-In, redirect to home
        // Small delay to ensure state is propagated
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            window.location.replace(localePath('/'));
          }
        }, 100);

        return user;
      }
      // No redirect result - this is normal when just loading the page
      return undefined;
    } catch (error) {
      set({ isLoading: false, isResolved: true });
      throw error;
    }
  },

  logout: async () => {
    // Set loading state to prevent UI flash during logout
    set({ isLoading: true });

    // Clear listeners first
    clearUserListener();

    /* Token push znika RAZEM z sesją. Na współdzielonym komputerze recepcji zostawiony
       wpis w `fcmTokens` wysyłałby powiadomienia poprzedniej zmiany na profil, przy
       którym siedzi już kto inny. Best-effort — brak sieci nie może blokować wylogowania. */
    const uid = get().currentUser?.uid;
    if (uid) {
      try {
        await unregisterWebPushToken(uid);
      } catch {
        /* ignore */
      }
    }

    // Clear storage before state reset to prevent any writes during cleanup
    try {
      // Clear user-specific items from localStorage
      localStorage.removeItem(LS_KEYS.USER_META);
      localStorage.removeItem('rememberMe');

      // Clear auth-related items
      Object.keys(localStorage).forEach((key) => {
        if (key.includes('auth') || key.includes('firebase') || key.includes('user')) {
          localStorage.removeItem(key);
        }
      });

      // Clear sessionStorage
      sessionStorage.clear();
    } catch {
      /* ignore */
    }

    // Sign out from Firebase first
    try {
      await signOut(auth);
    } catch (error) {
      // Ignore errors, continue with logout
    }

    // Reset organization state
    useOrganizationState.getState().reset();

    // The onAuthStateChanged listener will handle the final state update
    // We keep loading state true to prevent flash
  },

  resetPassword: async (email: string) => {
    // Mail wysyła NASZ backend (własny szablon, własna poczta) — klienckie
    // `sendPasswordResetEmail()` wysyłało wiadomość generowaną przez Firebase,
    // na której treść nie mamy wpływu. Endpoint odpowiada tak samo niezależnie
    // od tego, czy konto istnieje, więc nie zdradza, kto ma konto w HotelSpot.
    const baseURL = import.meta.env.VITE_URL || 'https://europe-west1-hotelspot-dev.cloudfunctions.net';

    const response = await fetch(`${baseURL}/v3-sendPanelPasswordReset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error('Nie udało się wysłać wiadomości z linkiem do zmiany hasła');
    }
  },

  confirmPasswordReset: async (code: string, newPassword: string) => {
    await firebaseConfirmPasswordReset(auth, code, newPassword);
  },

  setAvatar: (avatar: string | null) => {
    set({ avatar });
    const cached = readLS<Partial<UserState>>(LS_KEYS.USER_META) || {};
    writeLS(LS_KEYS.USER_META, { ...cached, avatar });
  },

  setUserLanguage: (language: string) => {
    set({ userLanguage: language });
    const cached = readLS<Partial<UserState>>(LS_KEYS.USER_META) || {};
    writeLS(LS_KEYS.USER_META, { ...cached, userLanguage: language });
  },

  setUser: (userData: Partial<UserState>) => {
    set(userData);
  },
}));

// Setup Firebase auth listener
onAuthStateChanged(auth, (user) => {
  // Każde nowe zdarzenie auth unieważnia oczekujący reset "braku usera".
  if (noUserResetTimer) {
    clearTimeout(noUserResetTimer);
    noUserResetTimer = null;
  }

  if (!user) {
    // No user - reset everything
    // Add a delay before setting loading to false to ensure smooth transition
    // This prevents flash during logout by keeping the loading spinner visible.
    // Cały teardown jest ODROCZONY do timera i chroniony strażnikiem
    // `auth.currentUser` — jeśli w międzyczasie user zdążył się (ponownie)
    // zalogować (przejściowy null), nic nie czyścimy.
    noUserResetTimer = setTimeout(() => {
      noUserResetTimer = null;
      if (auth.currentUser) return; // spurious null — user nadal zalogowany

      clearUserListener();
      useOrganizationState.getState().reset();

      // Clear cached user data (but preserve other localStorage items)
      try {
        localStorage.removeItem(LS_KEYS.USER_META);
      } catch {
        /* ignore */
      }

      useUserState.setState({
        ...initialState,
        isLoading: false,
        isResolved: true,
      });
    }, 300);
    return;
  }

  clearUserListener();

  // User exists - set basic auth data
  useUserState.setState({
    currentUser: user,
    userEmail: user.email ?? null,
    isLoading: false,
    isResolved: true,
  });

  // Load cached user data if valid
  const cachedUser = readLS<Partial<UserState>>(LS_KEYS.USER_META);
  if (cachedUser && cachedUser.userEmail === user.email) {
    useUserState.setState(cachedUser);
  }

  // Zasubskrybuj user document
  const userRef = doc(db, 'users', user.uid);
  userUnsub = onSnapshot(
    userRef,
    (snap) => {
      if (!snap.exists()) {
        // Zalogowane konto BEZ dokumentu personelu (np. gość, albo sesja
        // przywrócona po reloadzie) i bez claimu isStaff — nie wpuszczamy do
        // panelu. Wyloguj i zostaw monit dla strony logowania.
        try { sessionStorage.setItem(PANEL_ACCESS_DENIED_KEY, '1'); } catch { /* ignore */ }
        signOut(auth).catch(() => { /* ignore */ });
        return;
      }

      const d = snap.data() as any;
      const orgId: string | null = d.associated_organization ?? null;

      // Update user state (tylko dane profilu użytkownika)
      const userData: Partial<UserState> = {
        avatar: d.profilePicture || null,
        userName: d.firstName || d.name || null,
        organizationId: orgId,
        organizations: d.organizations ?? null,
        role: d.role ?? null,
        userLanguage: d.language || detectBrowserLanguage() || 'pl',
        tutorialCompleted: d.tutorialCompleted ?? false,
        isResolved: true,
      };

      useUserState.setState(userData);

      // Cache user data
      writeLS(LS_KEYS.USER_META, {
        avatar: userData.avatar,
        userName: userData.userName,
        role: userData.role,
        userLanguage: userData.userLanguage,
      });

      // Check organization and set flag
      const hasOrgId = !!(orgId && orgId.trim() !== '');
      const hasOrganization = hasOrgId;
      useUserState.setState({ hasOrganization });

      // Sync user language to languageState - ALWAYS override with user's preferred language from DB
      if (userData.userLanguage) {
        const currentLanguage = useLanguageState.getState().language;
        if (currentLanguage !== userData.userLanguage) {
          // User's language from DB is different - update languageState and i18n
          useLanguageState.getState().setLanguage(userData.userLanguage);

          // Also update i18n immediately
          import('../utils/i18n').then(({ i18n }) => {
            i18n.changeLanguage(userData.userLanguage);
          });
        }

        // Clean up preAuthLanguage - no longer needed after login
        try {
          localStorage.removeItem('preAuthLanguage');
        } catch {
          // ignore
        }
      }

      // Ustaw organizację w organizationState
      const userOrgs = d.organizations ?? [];

      // WAŻNE: Zawsze używaj associated_organization z Firebase jako źródła prawdy
      // localStorage jest tylko dla przywracania sesji po odświeżeniu strony,
      // nie dla przełączania organizacji
      let targetOrgId = orgId; // zawsze używamy associated_organization z Firebase

      // Sprawdź czy organizacja jest dostępna
      if (targetOrgId && !userOrgs.includes(targetOrgId)) {
        console.warn('User does not have access to associated_organization:', targetOrgId);
        // Jeśli użytkownik nie ma dostępu do associated_organization, ustaw null
        targetOrgId = null;
      }

      // Zapisz do localStorage dla przywracania po odświeżeniu
      if (targetOrgId) {
        try {
          localStorage.setItem('auth.lastOrgId', targetOrgId);
        } catch {
          /* ignore */
        }
      } else {
        // Wyczyść localStorage jeśli nie ma organizacji
        try {
          localStorage.removeItem('auth.lastOrgId');
          localStorage.removeItem('auth.org.meta');
        } catch {
          /* ignore */
        }
      }

      // Przekazujemy też organizations i testExpirationDate z user document
      useOrganizationState.getState().setOrganizationId(targetOrgId, {
        organizations: userOrgs,
        testExpirationDate: d.test_expiration_date ?? null,
      });
    },
    (error) => {
      useUserState.setState({ isResolved: true });
    },
  );
});

// Cleanup przy unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', clearUserListener);
}
